import { Icon } from "./icons"

// The pill-sized (12px) instances of the app's icon vocabulary for the
// 9pt origin / offering / capability pills and filter chips. The shapes and
// their single meanings live in icons.tsx — this file only names the
// instances the pills use. Stroke follows the surrounding text color.

// shopping cart — bought once
export const oneTimeIcon = <Icon name="purchase" />

// circular arrows — recurring billing
export const subscriptionIcon = <Icon name="subscription" />

// gift box — claimed for free
export const freeIcon = <Icon name="free" />

// award medal — claimed with reward points
export const rewardIcon = <Icon name="reward" />

// price tag — a redemption code (voucher)
export const codeIcon = <Icon name="code" />

// magic wand with sparkles — something appeared in the account by fiat
export const grantIcon = <Icon name="grant" />

// chain link — issued through a linked iLok account (the iLok origin pill).
// Not a key: the key is "a license" (the Licenses page), one meaning only.
export const ilokLinkedIcon = <Icon name="ilokLinked" />

// the iLok WORDMARK (243x69, tightly cropped), sized to match the text
// badges' content height: black for light grounds (the upgrade banner),
// white for dark ones (the Library's outline iLok-bound chip)
export const ilokIconBlack = (
  <img
    src="https://downloads.newfangledaudio.com/nfa/images/ilok-icon-black.png"
    height={9} alt="iLok" aria-hidden="true"
    style={{ display: "block", width: "auto" }}
  />
)

export const ilokIconWhite = (
  <img
    src="https://downloads.newfangledaudio.com/nfa/images/ilok-icon-white.png"
    height={9} alt="iLok" aria-hidden="true"
    style={{ display: "block", width: "auto" }}
  />
)

// eye — visible in the storefront (the aggregate "Shipping" filter)
export const shippingIcon = <Icon name="shipping" />

// eye with slash — hidden from the storefront
export const notShippedIcon = <Icon name="notShipped" />

// stack of layers — several products in one sku
export const bundleIcon = <Icon name="bundle" />

// the product glyph (sliders) — exactly one product; pairs with the stack
// above as "one plug-in / several"
export const singleIcon = <Icon name="product" />

// the A.L. wordmark (the assistant's brand logo) in its brand green. It's
// wide and literally reads "A.L", so badges and card titles show it ALONE —
// no text label next to it — with a title/aria label on the surrounding
// element carrying the meaning. Sized by height (12 for the 9pt pills,
// larger for card titles); width follows the mark's 2.56:1 aspect.
export function AlLogo({ height = 12 }: { height?: number }) {
  return (
    <svg width={Math.round(height * 2.56)} height={height} viewBox="915 170 2200 860" fill="#B6EDAA" aria-hidden="true">
    <g transform="matrix(2.16698,0,0,2.16698,483.031,-1262.14)">
      <g transform="matrix(1,0,0,1,0,-31.8284)">
        <path d="M456.131,962.333C456.131,991.52 480.166,1015.56 509.352,1015.56C538.538,1015.56 562.574,991.52 562.574,962.333C562.574,933.147 538.538,909.112 509.352,909.112C480.166,909.112 456.131,933.147 456.131,962.333Z" />
      </g>
      <path d="M476.733,771.766C483.6,763.181 496.476,748.588 509.352,748.588C522.228,748.588 535.105,762.323 542.83,771.766L742.841,1038.73C750.567,1049.03 762.584,1055.04 775.461,1055.04C797.779,1055.04 816.665,1036.16 816.665,1013.84C816.665,1002.68 811.514,992.378 804.647,984.652L596.052,705.668C576.309,679.057 543.689,663.605 509.352,663.605C472.44,663.605 438.962,681.632 419.219,708.243L210.624,988.944C204.615,995.812 202.04,1004.4 202.04,1013.84C202.04,1036.16 220.925,1055.04 243.244,1055.04C255.262,1055.04 267.279,1049.03 275.005,1039.59L476.733,771.766Z" />
      <g transform="matrix(0.545005,0,0,0.545005,254.232,460.186)">
        <path d="M902.506,1013.84C902.506,1043.02 926.542,1067.06 955.728,1067.06C984.914,1067.06 1008.95,1043.02 1008.95,1013.84C1008.95,984.652 984.914,960.617 955.728,960.617C926.542,960.617 902.506,984.652 902.506,1013.84Z" />
      </g>
      <g transform="matrix(1,0,0,1,-242.339,0)">
        <path d="M1212.39,1055.04L1412.8,1055.04C1435.12,1055.04 1454.01,1036.16 1454.01,1013.84C1454.01,991.52 1435.12,972.634 1412.8,972.634L1209.82,972.634C1192.65,972.634 1177.2,958.041 1177.2,940.873L1177.2,721.977C1177.2,699.659 1157.45,680.774 1136,680.774C1113.68,680.774 1094.79,699.659 1094.79,721.977L1094.79,940.873C1094.79,1004.4 1148.87,1055.04 1212.39,1055.04Z" />
      </g>
      <g transform="matrix(0.54522,0,0,0.54522,320.588,460.763)">
        <path d="M1510.87,1013.84C1510.87,1043.02 1534.9,1067.06 1564.09,1067.06C1593.27,1067.06 1617.31,1043.02 1617.31,1013.84C1617.31,984.652 1593.27,960.617 1564.09,960.617C1534.9,960.617 1510.87,984.652 1510.87,1013.84Z" />
      </g>
    </g>
    </svg>
  )
}

// the pill-sized instance for badges
export const alIcon = <AlLogo />

// lightning bolt — pay-as-you-go (runs on credits)
export const creditsIcon = <Icon name="credits" />

// clock — trial period
export const trialIcon = <Icon name="trial" />

// counter-clockwise arrow — a retroactive claim (deposited to owners of
// older versions); filled, unlike the stroke pills, to stay legible at 12px
export const retroactiveIcon = (
  <svg width="14.4" height="14.4" viewBox="0 0 16 16" fill="currentColor" style={{ display: "block" }}>
    <path fillRule="evenodd" d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2v1z" />
    <path d="M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466z" />
  </svg>
)

// the retroactive marker everywhere the word used to be — the tooltip
// carries the meaning the icon alone can't; optional text prefixes it
// ("now", "no longer") where the flag itself changed
export const retroactiveMark = (text?: string) => (
  <span
    title="Retroactive — owners of every older version are entitled too"
    style={{ display: "inline-flex", alignItems: "center", gap: 3, verticalAlign: "text-bottom" }}
  >
    {text}{retroactiveIcon}
  </span>
)

// trash bin — row delete buttons; takes the button's card-link-danger red
// via currentColor (bigger than the pills: it's a click target)
export const trashIcon = <Icon name="trash" size={18} />
